<template lang="pug">
div.px-8
  v-row(no-gutters)
    v-col.pr-8.shrink
      div.sticky.white(style="width: 150px; top: 106px;")
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/inventory')") Inventory
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/services')") Services
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/packages')") Packages
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/stocktakes')") Stock take
        .text-md.font-semibold.neutral200.px-6.py-2.round-md.mb-2(v-if="company.is_hq || company.branch_of") Branches & Warehouses
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/locations')") Locations
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(v-if="Auth('root', 'subscribe_purchase_hub')" @click="$router.push('/admin/clinic/inventory-list')") List
    v-col.grow
      v-container.py-8(fluid)
        //- Header
        v-row.mb-2(no-gutters align="center")
          v-col.shrink
            v-btn.ml-n3(text icon @click="goBack" height)
              v-icon(color="neutral700" :size="24") mdi-arrow-left
          v-col.ml-3
            .text-2xl.font-semibold.neutral700--text Branches & Warehouses

        //- Tabs and actions row
        .d-flex.align-center
          yz-tabs(v-model="activeTab" type="line")
            yz-tab(label="Branches")
            yz-tab(label="Warehouses")
          v-spacer
          .d-flex
            yz-search-btn.mr-2(v-model="searchText" @search="onSearch")
            //- Hide "Create new warehouse" button for non-HQ users
            yz-btn(v-if="isHqUser" label="Create new warehouse" size="lg" @click="onCreateWarehouse")

        yz-divider.ma-0

        //- Filter row
        branches-warehouses-filter-row(v-if="isInitialized" :filters.sync="filters" @reset="onResetFilters" @update:filters="onFiltersChange")

        //- Branches tab content
        branches-listing(v-if="isInitialized && activeTab === 0" ref="branchesListing" :filters="filters" :search-text="searchText" :is-hq-mode="isHqUser" :current-company-slug="company.slug")

        //- Warehouses tab content
        warehouses-listing(v-if="isInitialized && activeTab === 1" ref="warehousesListing" :filters="filters" :search-text="searchText" :is-hq-mode="isHqUser")
</template>

<script>
import { mapGetters } from 'vuex'
import BranchesWarehousesFilterRow from '@/components/hq/inventory/branches-warehouses/BranchesWarehousesFilterRow.vue'
import BranchesListing from '@/components/hq/inventory/branches-warehouses/BranchesListing.vue'
import WarehousesListing from '@/components/hq/inventory/branches-warehouses/WarehousesListing.vue'

export default {
  components: {
    BranchesWarehousesFilterRow,
    BranchesListing,
    WarehousesListing
  },
  data() {
    return {
      activeTab: 0,
      searchText: '',
      filters: [],
      isInitialized: false,
      tabNameToIndex: { branches: 0, warehouses: 1 },
      tabIndexToName: ['branches', 'warehouses']
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    routeQuery() {
      return this.$route.query
    },
    // Determine if current user is HQ
    isHqUser() {
      return this.company?.is_hq === true
    }
  },
  watch: {
    // Watch tab changes to update URL
    activeTab(newTab) {
      this.updateTabQuery(newTab)
    },
    // Watch URL tab changes (browser back/forward)
    '$route.query.tab': {
      handler(newTabName) {
        if (newTabName) {
          const tabIndex = this.tabNameToIndex[newTabName]
          if (tabIndex !== undefined && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      }
    },
    // Watch URL search changes
    '$route.query.search': {
      handler(newSearch) {
        const searchValue = newSearch || ''
        if (this.searchText !== searchValue) {
          this.searchText = searchValue
        }
      }
    },
    // Watch URL filters changes
    '$route.query.filters': {
      handler(newFilters) {
        const decoded = this.deserializeFilters(newFilters)
        if (JSON.stringify(this.filters) !== JSON.stringify(decoded)) {
          this.filters = decoded
        }
      }
    }
  },
  created() {
    this.initializeStateFromRoute()
    this.isInitialized = true
  },
  methods: {
    goBack() {
      this.$router.push('/admin/clinic/settings')
    },
    // Initialize state from URL on page load
    initializeStateFromRoute() {
      const { tab, search, filters } = this.$route.query

      // Initialize tab
      if (tab && this.tabNameToIndex[tab] !== undefined) {
        this.activeTab = this.tabNameToIndex[tab]
      } else {
        this.updateTabQuery(0)
      }

      // Initialize search
      if (search) {
        this.searchText = search
      }

      // Initialize filters
      if (filters) {
        this.filters = this.deserializeFilters(filters)
      }
    },
    // Update tab in URL
    updateTabQuery(tabIndex) {
      const tabName = this.tabIndexToName[tabIndex]
      if (tabName && this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: tabName }
        }).catch(() => {})
      }
    },
    // Update search in URL
    updateSearchQuery(search) {
      const newQuery = { ...this.$route.query }
      if (search) {
        newQuery.search = search
      } else {
        delete newQuery.search
      }
      if (this.$route.query.search !== newQuery.search) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    // Update filters in URL
    updateFiltersQuery(filters) {
      const newQuery = { ...this.$route.query }
      const serialized = this.serializeFilters(filters)
      if (serialized) {
        newQuery.filters = serialized
      } else {
        delete newQuery.filters
      }
      if (this.$route.query.filters !== newQuery.filters) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    // Serialize filters to base64
    serializeFilters(filters) {
      if (!filters || filters.length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    // Deserialize filters from base64
    deserializeFilters(base64String) {
      if (!base64String) {
        return []
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        return []
      }
    },
    onSearch(value) {
      this.searchText = value
      this.updateSearchQuery(value)
      if (this.$refs.branchesListing) {
        this.$refs.branchesListing.fetchBranches()
      }
      if (this.$refs.warehousesListing) {
        this.$refs.warehousesListing.fetchWarehouses()
      }
    },
    onResetFilters() {
      this.filters = []
      this.updateFiltersQuery([])
    },
    // Handle filter changes from FilterRow
    onFiltersChange(newFilters) {
      this.filters = newFilters
      this.updateFiltersQuery(newFilters)
    },
    onCreateWarehouse() {
      if (!this.isHqUser) return
      // If already on warehouses tab, open dialog directly
      if (this.activeTab === 1 && this.$refs.warehousesListing) {
        this.$refs.warehousesListing.openAddWarehouseDialog()
      } else {
        // Switch to warehouses tab first, then open dialog
        this.activeTab = 1
        this.$nextTick(() => {
          if (this.$refs.warehousesListing) {
            this.$refs.warehousesListing.openAddWarehouseDialog()
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
